import { useRef, useState } from "react";
import { Loader2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { API, authHeaders } from "./adminApi";

export const ImageUploadField = ({ value, onChange, testIdPrefix = "image" }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type)) {
      toast.error("Please choose a JPG, PNG, WEBP, or GIF image");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be under 8MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/api/admin/upload-image`, {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "Upload failed");
        return;
      }
      onChange(data.url);
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const preview = value ? (value.startsWith("/") ? `${API}${value}` : value) : "";

  return (
    <div>
      <Label className="font-body text-sm">Image</Label>
      <div className="flex gap-2 items-center">
        <Input
          data-testid={`${testIdPrefix}-url`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://... or upload →"
          className="font-body flex-1"
        />
        <Button
          data-testid={`${testIdPrefix}-upload-button`}
          type="button"
          variant="outline"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="font-body rounded-full shrink-0"
        >
          {uploading ? <Loader2 size={15} className="animate-spin" /> : <><Upload size={14} className="mr-1" /> Upload</>}
        </Button>
        <input ref={fileRef} data-testid={`${testIdPrefix}-file-input`} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={handleFile} />
      </div>
      {preview && (
        <img src={preview} alt="Preview" className="mt-2 h-20 w-32 object-cover rounded-md border border-border/60" data-testid={`${testIdPrefix}-preview`} />
      )}
    </div>
  );
};
